import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { RecurringPaymentInstruction } from '../../../../../models/submissions/recurring-payment-instruction';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import * as moment from 'moment';
import { VbPaymentChangePaymentsStepUtils } from '../../create-vb-payment-change-components/utils/vb-payment-change-payments-step.utils';
import {
  VbPaymentChangeReviewStepUtils,
  VbScheduledChangeNotice
} from '../../create-vb-payment-change-components/utils/vb-payment-change-review-step.utils';

export enum VbRecurringPaymentChangeTypeEnum {
  Current = 'Current',
  New = 'New'
}

@Component({
  selector: 'app-vb-payment-change-submission-recurring-payments-card',
  templateUrl: './vb-payment-change-submission-recurring-payments-card.component.html',
  styleUrls: ['./vb-payment-change-submission-recurring-payments-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VbPaymentChangeSubmissionRecurringPaymentsCardComponent implements OnChanges {
  @Input() newRecurringPaymentsForgone: boolean = false;
  @Input() cutoffDate: string | null = null;
  @Input() allowableChangeDate: string | null = null;

  @Input() previousActiveMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() previousScheduledMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() previousActiveAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() previousScheduledAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;

  @Input() newActiveMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() newScheduledMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() newActiveAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  @Input() newScheduledAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;

  @Input() monthlyScheduledChangeNotice: VbScheduledChangeNotice | null = null;
  @Input() annualScheduledChangeNotice: VbScheduledChangeNotice | null = null;

  public monthlyHasChanged: boolean = false;
  public annualHasChanged: boolean = false;

  public displayNewActiveMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  public displayNewScheduledMonthlyRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  public displayNewActiveAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;
  public displayNewScheduledAnnualRecurringPaymentInstruction: RecurringPaymentInstruction | null = null;

  public submittedAfterCutoffDate: boolean = false;
  public paymentCeaseDate: string | null = null;
  public scheduledChangeNotices: VbScheduledChangeNotice[] = [];

  public ngOnChanges(): void {
    this.submittedAfterCutoffDate = VbPaymentChangePaymentsStepUtils.isPastCutoff(this.cutoffDate);
    this.paymentCeaseDate = this.allowableChangeDate ? moment.utc(this.allowableChangeDate).format('MMMM, YYYY') : null;
    this.scheduledChangeNotices = [this.monthlyScheduledChangeNotice, this.annualScheduledChangeNotice].filter(
      (notice): notice is VbScheduledChangeNotice => notice !== null
    );

    const monthlyScheduledWasRemoved = this.isScheduledRemoved(
      this.previousScheduledMonthlyRecurringPaymentInstruction,
      this.previousActiveMonthlyRecurringPaymentInstruction,
      this.newScheduledMonthlyRecurringPaymentInstruction
    );

    const annualScheduledWasRemoved = this.isScheduledRemoved(
      this.previousScheduledAnnualRecurringPaymentInstruction,
      this.previousActiveAnnualRecurringPaymentInstruction,
      this.newScheduledAnnualRecurringPaymentInstruction
    );

    this.displayNewActiveMonthlyRecurringPaymentInstruction = monthlyScheduledWasRemoved
      ? this.previousActiveMonthlyRecurringPaymentInstruction
      : this.newActiveMonthlyRecurringPaymentInstruction;

    this.displayNewScheduledMonthlyRecurringPaymentInstruction = monthlyScheduledWasRemoved
      ? null
      : this.newScheduledMonthlyRecurringPaymentInstruction;

    this.displayNewActiveAnnualRecurringPaymentInstruction = annualScheduledWasRemoved
      ? this.previousActiveAnnualRecurringPaymentInstruction
      : this.newActiveAnnualRecurringPaymentInstruction;

    this.displayNewScheduledAnnualRecurringPaymentInstruction = annualScheduledWasRemoved
      ? null
      : this.newScheduledAnnualRecurringPaymentInstruction;

    this.monthlyHasChanged =
      monthlyScheduledWasRemoved ||
      JSON.stringify(this.previousActiveMonthlyRecurringPaymentInstruction) !==
        JSON.stringify(this.newActiveMonthlyRecurringPaymentInstruction) ||
      JSON.stringify(this.previousScheduledMonthlyRecurringPaymentInstruction) !==
        JSON.stringify(this.newScheduledMonthlyRecurringPaymentInstruction);

    this.annualHasChanged =
      annualScheduledWasRemoved ||
      JSON.stringify(this.previousActiveAnnualRecurringPaymentInstruction) !==
        JSON.stringify(this.newActiveAnnualRecurringPaymentInstruction) ||
      JSON.stringify(this.previousScheduledAnnualRecurringPaymentInstruction) !==
        JSON.stringify(this.newScheduledAnnualRecurringPaymentInstruction);
  }

  private isScheduledRemoved(
    previousScheduled: RecurringPaymentInstruction | null,
    previousActive: RecurringPaymentInstruction | null,
    newScheduled: RecurringPaymentInstruction | null
  ): boolean {
    return (
      previousScheduled !== null &&
      previousActive !== null &&
      newScheduled !== null &&
      VbPaymentChangeReviewStepUtils.sameRecurringAmount(newScheduled, previousActive)
    );
  }

  protected readonly VariableBenefitPaymentFrequencyEnum = VariableBenefitPaymentFrequencyEnum;
  protected readonly VbRecurringPaymentChangeTypeEnum = VbRecurringPaymentChangeTypeEnum;
}
